// eslint-disable-next-line @typescript-eslint/ban-ts-comment
//@ts-ignore
import {
    ReadonlyMat3,
    ReadonlyVec2,
    ReadonlyVec3,
    mat3,
    vec2,
    vec3,
} from 'gl-matrix';
import {
    dcmjsGetString,
    dicomSlicePixelDataToFloat32,
    dicomSlicePixelDataToInternal,
} from '../services/dicomProcessor';
import type { NaturalizedDataset } from 'dcmjs';
import { DcmJsDatasetWrapper } from './dcmJsWrapper';
import { DicomBase } from './dicomBase';
import { PIXEL_DATA_TAG } from './engine/tag';
import * as tags from './engine/tags';
import { parseDicomDateTime } from './utils';
import { WindowLevel } from '../patient/windowLevel';
import { PetSeriesMetadata } from '../patient/petSuv';
import { Modality } from './modality';

/**
 * The earlier of two DICOM DateTime strings (QIBA's vendor-neutral scan-time
 * rule, as mvdicom implements it in ct_mr.py:1310-1345): some vendors stamp
 * SeriesTime with the reconstruction time, and using it would inflate the
 * decay interval and systematically overestimate SUV. An absent or
 * unparseable value never wins — a malformed candidate is treated as absent
 * rather than allowed to shadow a valid one. On a tie the FIRST argument
 * wins, so callers pass the preferred source (series datetime) first.
 */
export function earlierDicomDateTime(
    preferred: string | undefined,
    other: string | undefined
): string | undefined {
    const preferredMs =
        preferred !== undefined ? parseDicomDateTime(preferred) : NaN;
    const otherMs = other !== undefined ? parseDicomDateTime(other) : NaN;
    if (!Number.isFinite(preferredMs)) {
        return Number.isFinite(otherMs) ? other : undefined;
    }
    if (!Number.isFinite(otherMs)) {
        return preferred;
    }
    return otherMs < preferredMs ? other : preferred;
}

/**
 * CT acquisition display attributes, formatted by
 * `getCTModalitySpecificAttributes` (Protocol Name rides here because
 * the same parser reads it for every modality).
 */
export interface CtAcquisitionAttributes {
    kvp?: string;
    ctdi?: string;
    kernel?: string;
    protocolName?: string;
}

/**
 * MR acquisition display attributes, formatted by
 * `getMRModalitySpecificAttributes`.
 */
export interface MrAcquisitionAttributes {
    scanningSequence?: string;
    mraAcquisitionType?: string;
    sequenceVariant?: string;
    sequenceName?: string;
    repetitionTime?: string;
    echoTime?: string;
    inversionTime?: string;
    flipAngle?: string;
    numberOfAverages?: string;
    magneticFieldStrength?: string;
}

/**
 * PET radiopharmaceutical attributes, parsed by
 * `getPetSpecificAttributes`.
 */
export interface PetAcquisitionAttributes {
    radiopharmaceutical?: string;
    radionuclideTotalDose?: number;
    radionuclideHalfLife?: number;
    radiopharmaceuticalStartTime?: string;
    radiopharmaceuticalStartDateTime?: string;
}

/**
 * The acquisition-context tail of the {@link DicomImageSlice}
 * constructor: timing/weight plus the per-modality groups, keyed by the
 * parser that produced them. Grouped because as flat positional
 * parameters these were 22 adjacent interchangeable `string | undefined`
 * slots, where a transposition type-checks silently.
 */
export interface DicomImageSliceAttributes {
    patientWeightKg?: number;
    seriesTime?: string;
    acquisitionTime?: string;
    studyDate?: string;
    seriesDate?: string;
    acquisitionDate?: string;
    ct?: CtAcquisitionAttributes;
    mr?: MrAcquisitionAttributes;
    pet?: PetAcquisitionAttributes;
}

/** How a slice's stored pixel values are laid out. */
interface PixelEncoding {
    readonly pixelRepresentation: number;
    readonly bitsAllocated: number;
    readonly bitsStored: number;
}

export class DicomImageSlice extends DicomBase {
    /**
     * Physical distance in between the center of each pixel, specified by a numeric pair.\
     * The pixelSpacing in planar directions should stay constant between slices.
     * (0028,0030) https://dicom.innolitics.com/ciods/ct-image/image-plane/00280030
     */
    public readonly pixelSpacing: ReadonlyVec2;

    /**
     * The x, y, and z coordinates of the upper left hand corner (center of the first voxel transmitted) of the image, in mm.\
     * These are the PatientSpace coordinates.\
     * Note the coordinates are for the CENTER of the first voxel.\
     * So half a pixel offset is often needed to get more meaningful coordinates.
     * (0020,0032) https://dicom.innolitics.com/ciods/ct-image/image-plane/00200032
     */
    public readonly imagePosition: ReadonlyVec3;

    /**
     * The thickness of the slice in mm.\
     * (0018,0050) https://dicom.innolitics.com/ciods/ct-image/image-plane/00180050
     */
    public readonly sliceThickness: number;

    /**
     * This is the rotation of the Image in PatientSpace.\
     * By applying this matrix to a vertex, it can be converted from ImageSpace to PatientSpace.
     * (0020,0037) https://dicom.innolitics.com/ciods/ct-image/image-plane/00200037
     */
    public readonly imageOrientation: ReadonlyMat3;

    /**
     * A description of the Series.\
     * (0008,103e) https://dicom.innolitics.com/ciods/ct-image/general-series/0008103e
     */
    public readonly seriesDescription: string;

    /**
     * The Series Instance UID.\
     * (0020,000e) https://dicom.innolitics.com/ciods/ct-image/general-series/0020000e
     */
    public readonly seriesInstanceUid: string;

    /**
     * The Frame of Reference UID.\
     * (0020,0052) https://dicom.innolitics.com/ciods/ct-image/general-image/00200052
     */
    public readonly frameOfReferenceUid: string;

    /**
     * The manufacturer of the equipment that produced the image.\
     * (0008,0070) https://dicom.innolitics.com/ciods/ct-image/general-image/00080070
     */
    public readonly manufacturer: string;

    /**
     * The manufacturer's model name of the equipment that produced the image.\
     * (0008,1090) https://dicom.innolitics.com/ciods/ct-image/general-equipment/00081090
     */
    public readonly manufacturerModelName: string;

    /**
     * The instance number of the image.\
     * (0020,0013) https://dicom.innolitics.com/ciods/ct-image/general-image/00200013
     */
    public readonly instanceNumber: number;

    /**
     * Image identification characteristics, ['ORIGINAL', 'PRIMARY'] etc.\
     * 	(0008,0008) https://dicom.innolitics.com/ciods/ct-image/general-image/00080008
     */
    public readonly imageType: string[];

    /**
     * Number of pixels in image's vertical direction.\
     * This is the {@link Image image}'s pixel size along y-axis.\
     * (0028, 0010) https://dicom.innolitics.com/ciods/ct-image/image-pixel/00280010
     */
    public readonly rows: number;

    /**
     * Number of pixels in image's horizontal direction.\
     * This is the {@link Image image}'s pixel size along x-axis.\
     * (0028, 0011) https://dicom.innolitics.com/ciods/ct-image/image-pixel/00280011
     */
    public readonly cols: number;

    /**
     * Patient position during imaging.\
     * (0018,5100) https://dicom.innolitics.com/ciods/ct-image/ct-acquisition/00185100
     */
    public readonly patientPosition: string;

    /**
     * Name of the station that produced the image.\
     * (0008,1010) https://dicom.innolitics.com/ciods/ct-image/general-equipment/00081010
     */
    public readonly stationName: string;

    /**
     * The type of filter used in the image.\
     * (0018,1160) https://dicom.innolitics.com/ciods/ct-image/ct-acquisition/00181160
     */
    public readonly filterType: string;

    /**
     * The intercept of the linear relationship between the stored values in the image and the pixel values in image output units.\
     * (0028,1052) https://dicom.innolitics.com/ciods/ct-image/ct-image/00281052
     */
    public readonly intercept: number = 0;

    /**
     * The slope of the linear relationship between the stored values in the image and the pixel values in image output units.\
     * (0028,1052) https://dicom.innolitics.com/ciods/ct-image/ct-image/00281052
     */
    public readonly slope: number = 1.0;

    /**
     * The peak kilovoltage output of the X-ray generator used.\
     * (0018,0060) https://dicom.innolitics.com/ciods/ct-image/ct-acquisition/00180060
     */

    public readonly kvp?: string;

    /**
     * The computed tomography dose index.\
     * (0018,0093) https://dicom.innolitics.com/ciods/ct-image/ct-acquisition/00180093
     */

    public readonly ctdi?: string;

    /**
     * The convolution kernel used for reconstruction.\
     * (0018,9311) https://dicom.innolitics.com/ciods/ct-image/ct-acquisition/00189311
     */

    public readonly kernel?: string;

    /**
     * The sequence of the scanning sequence.\
     * (0018,0020) https://dicom.innolitics.com/ciods/ct-image/ct-acquisition/00180020
     */

    public readonly scanningSequence?: string;

    /**
     * The type of MRA acquisition.\
     * (0018,0023) https://dicom.innolitics.com/ciods/ct-image/ct-acquisition/00180023
     */

    public readonly mraAcquisitionType?: string;

    /**
     * The sequence variant.\
     * (0018,0024) https://dicom.innolitics.com/ciods/ct-image/ct-acquisition/00180024
     */

    public readonly sequenceVariant?: string;

    /** Sequence name (0018,0024) */
    public readonly sequenceName?: string;

    /** Repetition time in ms (0018,0080) */
    public readonly repetitionTime?: string;

    /** Echo time in ms (0018,0081) */
    public readonly echoTime?: string;

    /** Inversion time in ms (0018,0082) */
    public readonly inversionTime?: string;

    /** Flip angle in degrees (0018,1314) */
    public readonly flipAngle?: string;

    /** Number of averages (0018,0083) */
    public readonly numberOfAverages?: string;

    /**
     * Magnetic field strength (0018,0087) — in T only when plausible,
     * else the raw value (see getMRModalitySpecificAttributes).
     */
    public readonly magneticFieldStrength?: string;

    /**
     * The protocol name.\
     * (0018,1030) https://dicom.innolitics.com/ciods/ct-image/ct-acquisition/00181030
     */

    public readonly protocolName?: string;

    /** Patient weight in kg (0010,1030) */
    public readonly patientWeightKg?: number;

    /**
     * Radionuclide total dose injected in Bq.
     * (0054,0016)[0].(0018,1074)
     */
    public readonly radionuclideTotalDose?: number;

    /**
     * Radionuclide half life in seconds.
     * (0054,0016)[0].(0018,1075)
     */
    public readonly radionuclideHalfLife?: number;

    /**
     * Radiopharmaceutical start time (injection time) as DICOM time string.
     * (0054,0016)[0].(0018,1072)
     */
    public readonly radiopharmaceuticalStartTime?: string;

    /**
     * Radiopharmaceutical start datetime as DICOM datetime string.
     * (0054,0016)[0].(0018,1078)
     */
    public readonly radiopharmaceuticalStartDateTime?: string;

    /**
     * Radiopharmaceutical label, best source first:
     * RadiopharmaceuticalCodeSequence (0054,0304)[0] CodeMeaning,
     * free-text Radiopharmaceutical (0018,0031),
     * RadionuclideCodeSequence (0054,0300)[0] CodeMeaning.
     * All within the (0054,0016) item.
     */
    public readonly radiopharmaceutical?: string;

    /**
     * Series time as DICOM time string.
     * (0008,0031)
     */
    public readonly seriesTime?: string;

    /**
     * Acquisition time as DICOM time string.
     * (0008,0032)
     */
    public readonly acquisitionTime?: string;

    /** Acquisition date (0008,0022) */
    public readonly acquisitionDate?: string;

    /** Series date (0008,0021) */
    public readonly seriesDate?: string;

    /** Study date (0008,0020) */
    public readonly studyDate?: string;

    /**
     * The pixel data of the image, converted to external values by using intercept and slope.\
     * (7fe0,0010) https://dicom.innolitics.com/ciods/ct-image/pixel-data/7fe00010
     *
     * Not available for PET images — rescaled values can exceed Int16 range.
     * Use {@link floatPixelData} instead for PET.
     */
    private _pixelData?: Int16Array;
    public get pixelData(): Int16Array {
        if (this.modality === Modality.PT) {
            throw new Error(
                'Int16 pixel data is not available for PET images — ' +
                    'rescaled values exceed Int16 range. ' +
                    'Use floatPixelData.'
            );
        }
        if (!this._pixelData) {
            this._pixelData = this.toInternalPixelData();
        }
        return this._pixelData;
    }

    /**
     * Float32 pixel data with rescale slope/intercept applied.
     * Safe for all modalities including PET where values exceed Int16 range.
     */
    private _floatPixelData?: Float32Array;
    public get floatPixelData(): Float32Array {
        if (!this._floatPixelData) {
            this._floatPixelData = this.toFloat32PixelData();
        }
        return this._floatPixelData;
    }

    /**
     * Whether the patient is in head first prone position.\
     */
    public readonly headFirstProne: boolean;

    /**
     * Whether the dataset is in a tilted position.\
     */
    public readonly tilted: boolean;

    private _windowLevel: WindowLevel | undefined;

    public get windowLevel(): WindowLevel | undefined {
        return this._windowLevel;
    }

    public set windowLevel(windowLevel: WindowLevel | undefined) {
        this._windowLevel = windowLevel;
    }

    constructor(
        sopUid: string,
        modality: string,
        pixelSpacing: vec2,
        imagePosition: vec3,
        imageOrientation: mat3,
        sliceThickness: number,
        seriesDescription: string,
        seriesInstanceUid: string,
        frameOfReferenceUid: string,
        manufacturer: string,
        manufacturerModelName: string,
        instanceNumber: number,
        imageType: string[],
        rows: number,
        cols: number,
        patientPosition: string,
        stationName: string,
        filterType: string,
        intercept: number,
        slope: number,
        headFirstProne: boolean,
        tilted: boolean,
        dataset: DcmJsDatasetWrapper,
        attributes: DicomImageSliceAttributes = {}
    ) {
        super(sopUid, modality, dataset);
        this.pixelSpacing = pixelSpacing;
        this.imagePosition = imagePosition;
        this.imageOrientation = imageOrientation;
        this.seriesDescription = seriesDescription;
        this.sliceThickness = sliceThickness;
        this.seriesDescription = seriesDescription;
        this.seriesInstanceUid = seriesInstanceUid;
        this.frameOfReferenceUid = frameOfReferenceUid;
        this.manufacturer = manufacturer;
        this.manufacturerModelName = manufacturerModelName;
        this.instanceNumber = instanceNumber;
        this.imageType = imageType;
        this.rows = rows;
        this.cols = cols;
        this.patientPosition = patientPosition;
        this.stationName = stationName;
        this.filterType = filterType;
        this.intercept = intercept;
        this.slope = slope;
        this.headFirstProne = headFirstProne;
        this.tilted = tilted;
        this.kvp = attributes.ct?.kvp;
        this.ctdi = attributes.ct?.ctdi;
        this.kernel = attributes.ct?.kernel;
        this.protocolName = attributes.ct?.protocolName;
        this.scanningSequence = attributes.mr?.scanningSequence;
        this.mraAcquisitionType = attributes.mr?.mraAcquisitionType;
        this.sequenceVariant = attributes.mr?.sequenceVariant;
        this.sequenceName = attributes.mr?.sequenceName;
        this.repetitionTime = attributes.mr?.repetitionTime;
        this.echoTime = attributes.mr?.echoTime;
        this.inversionTime = attributes.mr?.inversionTime;
        this.flipAngle = attributes.mr?.flipAngle;
        this.numberOfAverages = attributes.mr?.numberOfAverages;
        this.magneticFieldStrength = attributes.mr?.magneticFieldStrength;
        this.patientWeightKg = attributes.patientWeightKg;
        this.radionuclideTotalDose = attributes.pet?.radionuclideTotalDose;
        this.radionuclideHalfLife = attributes.pet?.radionuclideHalfLife;
        this.radiopharmaceuticalStartTime =
            attributes.pet?.radiopharmaceuticalStartTime;
        this.radiopharmaceuticalStartDateTime =
            attributes.pet?.radiopharmaceuticalStartDateTime;
        this.radiopharmaceutical = attributes.pet?.radiopharmaceutical;
        this.seriesTime = attributes.seriesTime;
        this.acquisitionTime = attributes.acquisitionTime;
        this.studyDate = attributes.studyDate;
        this.seriesDate = attributes.seriesDate;
        this.acquisitionDate = attributes.acquisitionDate;
    }

    public get studyInstanceUID(): string {
        return dcmjsGetString(this.datasetWrapper, 'StudyInstanceUID');
    }

    /**
     * DICOM `BurnedInAnnotation` (0028,0301) for this slice — whether identifying
     * text is burned into the pixel data — or `''` when the (optional) attribute
     * is absent. Classify with `parseBurnedInAnnotation`: an absent value is
     * `Unknown`, never silently "safe".
     */
    public get burnedInAnnotation(): string {
        return dcmjsGetString(this.datasetWrapper, 'BurnedInAnnotation');
    }

    /**
     * DICOM `RescaleType` (0028,1054) — the output unit of the rescale
     * slope/intercept transform — or `''` when the (optional) attribute is
     * absent. Absence does NOT mean the values are not HU: most CT series
     * omit the tag and are HU by the CT IOD's definition. Classification
     * belongs to {@link Image3D.isHuQuantified}; this getter only reports
     * what the dataset says.
     */
    public get rescaleType(): string {
        return dcmjsGetString(this.datasetWrapper, 'RescaleType');
    }

    /**
     * DICOM `Units` (0054,1001) — the unit of the stored PET voxel values —
     * or `''` when absent. `BQML` (activity concentration, Bq/ml) is what
     * the SUVbw conversion `raw × factor` assumes; a `GML` series (vendor
     * pre-computed SUV) or `CNTS` (counts) would be silently mis-scaled by
     * it. Classification belongs to {@link Image3D.isSuvQuantified}; this
     * getter only reports what the dataset says.
     */
    public get units(): string {
        return dcmjsGetString(this.datasetWrapper, 'Units');
    }

    /**
     * DICOM `DecayCorrection` (0054,1102) — the reference time the stored
     * activity is decay-corrected to — or `''` when absent. The SUVbw
     * formula decays the injected dose from injection to SCAN START, so it
     * assumes `START`; `NONE`/`ADMIN` make its decay term wrong.
     * Classification belongs to {@link Image3D.isSuvQuantified}.
     */
    public get decayCorrection(): string {
        return dcmjsGetString(this.datasetWrapper, 'DecayCorrection');
    }

    /**
     * DICOM `SUVType` (0054,1006) — which normalization a pre-converted
     * (`Units=GML`) series carries (BW, LBM, BSA, IBW) — or `''` when
     * absent. Absent on a GML series implies BW, but that inference
     * belongs to {@link classifySuvQuantification}; this getter only
     * reports what the dataset says.
     */
    public get suvType(): string {
        return dcmjsGetString(this.datasetWrapper, 'SUVType');
    }

    /**
     * DICOM `CorrectedImage` (0028,0051) — which corrections have been
     * applied to this PET reconstruction — as a trimmed/uppercased list,
     * or `null` when the attribute is absent or empty (Type 2 in the PET
     * Series Module, PS3.3 Table C.8-60 — required, zero-length when the
     * corrections are unknown). Either way the
     * applied corrections are UNKNOWN, never "none were needed": vendors
     * routinely export attenuation-corrected (AC) and non-attenuation-
     * corrected (NAC) reconstructions of the SAME acquisition with
     * identical Units/DecayCorrection/radiopharmaceutical metadata, and
     * only this attribute tells them apart. Classification belongs to
     * {@link classifySuvQuantification} (which mirrors mvdicom in
     * refusing SUV without it); this getter only reports what the
     * dataset says. dcmjs naturalizes a single-valued CS to a bare
     * string and a multi-valued one to an array — both shapes are
     * normalized to a list here so the classifier sees one contract.
     */
    public get correctedImage(): readonly string[] | null {
        const dataset = this.datasetWrapper.dataset as {
            CorrectedImage?: unknown;
        } | null;
        const raw = dataset?.CorrectedImage;
        if (raw === undefined || raw === null) {
            return null;
        }
        const values: unknown[] = Array.isArray(raw) ? raw : [raw];
        return values.map((value) => String(value).trim().toUpperCase());
    }

    /**
     * Series datetime (0008,0021 + 0008,0031) as a DICOM DateTime string,
     * falling back to studyDate + seriesTime; undefined when neither pair
     * is complete.
     */
    public get resolvedSeriesDateTime(): string | undefined {
        if (this.seriesDate && this.seriesTime) {
            return this.seriesDate + this.seriesTime;
        }
        if (this.studyDate && this.seriesTime) {
            return this.studyDate + this.seriesTime;
        }
        return undefined;
    }

    /**
     * THIS slice's acquisition datetime (0008,0022 + 0008,0032) as a DICOM
     * DateTime string, falling back to studyDate/seriesDate for the date
     * part; undefined when no acquisition time exists. On multi-bed PET
     * each bed position stamps its own acquisition time, so a volume-level
     * consumer must take the minimum across ALL slices
     * ({@link Image3D.petMetadata}) — never trust one slice's value alone.
     */
    public get resolvedAcquisitionDateTime(): string | undefined {
        if (this.acquisitionDate && this.acquisitionTime) {
            return this.acquisitionDate + this.acquisitionTime;
        }
        const date = this.studyDate ?? this.seriesDate;
        if (this.acquisitionTime && date) {
            return date + this.acquisitionTime;
        }
        return undefined;
    }

    /**
     * Resolves the best available scan datetime for THIS slice as a DICOM
     * DateTime string: the earlier of the series and acquisition datetimes
     * ({@link earlierDicomDateTime} — the QIBA rule), whichever exists when
     * only one does. Single-slice contexts only: the volume-level SUV
     * verdict must use the series-wide minimum acquisition datetime
     * ({@link Image3D.petMetadata}) because dicomImageSlices order is file
     * arrival order — nondeterministic — and on multi-bed PET an arbitrary
     * bed's acquisition time can be many minutes late.
     */
    public get resolvedScanDateTime(): string | undefined {
        return earlierDicomDateTime(
            this.resolvedSeriesDateTime,
            this.resolvedAcquisitionDateTime
        );
    }

    /**
     * Resolves the best available injection datetime as a DICOM DateTime string.
     * Prefers radiopharmaceuticalStartDateTime (already date+time),
     * falls back to studyDate + radiopharmaceuticalStartTime.
     */
    public get resolvedInjectionDateTime(): string | undefined {
        if (this.radiopharmaceuticalStartDateTime) {
            return this.radiopharmaceuticalStartDateTime;
        }
        if (
            this.radiopharmaceuticalStartTime &&
            (this.studyDate ?? this.acquisitionDate ?? this.seriesDate)
        ) {
            const date =
                this.studyDate ?? this.acquisitionDate ?? this.seriesDate;
            return date + this.radiopharmaceuticalStartTime;
        }
        return undefined;
    }

    /**
     * Returns resolved PET metadata with single datetime fields.
     */
    public get petMetadata(): PetSeriesMetadata | null {
        if (this.modality !== Modality.PT) {
            return null;
        }
        return {
            patientWeightKg: this.patientWeightKg,
            radionuclideTotalDose: this.radionuclideTotalDose,
            radionuclideHalfLife: this.radionuclideHalfLife,
            scanDateTime: this.resolvedScanDateTime,
            injectionDateTime: this.resolvedInjectionDateTime,
        };
    }

    /**
     * The pixel value and the encoding needed to interpret it.
     *
     * Read through the typed model where the slice was parsed by this
     * application, which is the ordinary case: the value is then a view over
     * the buffer the file already occupies, rather than a copy the
     * keyword-addressed projection makes of it. For a streamed series that
     * difference is one whole extra copy of every slice held for as long as
     * the image is open.
     *
     * Falls back for a dataset assembled in memory, and for one whose pixel
     * data has been decompressed in place — neither has a native value in the
     * typed model to read.
     */
    private getRawPixelBytes(): [ArrayBuffer | Uint8Array, PixelEncoding] {
        const typed = this.datasetWrapper.typed;
        const bytes = typed?.bytes(PIXEL_DATA_TAG);
        if (typed && bytes) {
            return [
                bytes,
                {
                    pixelRepresentation: typed.number(
                        tags.PIXEL_REPRESENTATION
                    ) as number,
                    bitsAllocated: typed.number(tags.BITS_ALLOCATED) as number,
                    bitsStored: typed.number(tags.BITS_STORED) as number,
                },
            ];
        }

        const dataset = this.datasetWrapper.dataset as NaturalizedDataset;
        return [
            this.datasetWrapper.rawPixelDataBuffer(),
            {
                pixelRepresentation: dataset.PixelRepresentation as number,
                bitsAllocated: dataset.BitsAllocated as number,
                bitsStored: dataset.BitsStored as number,
            },
        ];
    }

    toInternalPixelData(): Int16Array {
        const [pixels, encoding] = this.getRawPixelBytes();
        return dicomSlicePixelDataToInternal(
            encoding.pixelRepresentation,
            pixels,
            encoding.bitsAllocated,
            encoding.bitsStored,
            this.slope,
            this.intercept
        );
    }

    toFloat32PixelData(): Float32Array {
        const [pixels, encoding] = this.getRawPixelBytes();
        return dicomSlicePixelDataToFloat32(
            encoding.pixelRepresentation,
            pixels,
            encoding.bitsAllocated,
            encoding.bitsStored,
            this.slope,
            this.intercept
        );
    }
}
